import { useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { ClockRewindHistory, ContractAlignRight } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectUpdateHistoryForAsset } from '../../store/selectors'
import { TimelineAssetActions } from '../../store/slice'
import { kActiveTimelineUpdateStatuses, TimelineUpdateKind, TimelineUpdateStatus, type TimelineUpdateRead } from '../../store/schemas'

// Renders each row's timestamp as "MM/DD/YYYY  h:mm AM/PM" (two spaces between date and time).
function formatVersionDateTime(ms: number): string {
  const d = new Date(ms)
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date}  ${time}`
}

/**
 * The committed-version description that backs a row's secondary line: each version shows the real
 * operation that produced it.
 */
function describeUpdate(update: TimelineUpdateRead): string {
  const added = update.events_added ?? 0
  const removed = update.events_removed ?? 0
  switch (update.kind) {
    case TimelineUpdateKind.INITIALIZE:
      return `Timeline generated · ${added} event${added === 1 ? '' : 's'}`
    case TimelineUpdateKind.ADD_FILES: {
      const files = update.files_added ?? 0
      const events = added ? ` · +${added} event${added === 1 ? '' : 's'}` : ' · no new events'
      return `Added ${files} file${files === 1 ? '' : 's'}${events}`
    }
    case TimelineUpdateKind.REMOVE_FILE:
      return removed ? `Removed a file · −${removed} event${removed === 1 ? '' : 's'}` : 'Removed a file'
    default:
      // Exhaustiveness: a newly added TimelineUpdateKind is a compile error here instead
      // of silently rendering a blank version line.
      update.kind satisfies never
      return ''
  }
}

function describeActiveUpdate(update: TimelineUpdateRead): string {
  switch (update.kind) {
    case TimelineUpdateKind.INITIALIZE:
      return 'Generating timeline…'
    case TimelineUpdateKind.ADD_FILES: {
      const files = update.files_requested
      return `Adding ${files} file${files === 1 ? '' : 's'}…`
    }
    case TimelineUpdateKind.REMOVE_FILE:
      return 'Removing a file…'
    default:
      update.kind satisfies never
      return ''
  }
}

type VersionRow = {
  id: string
  versionLabel: string | null
  when: string
  label: string
  active: boolean
  failed: boolean
  current: boolean
}

/**
 * Builds the display rows from the raw update history: a version tag + timestamp per row, the newest
 * committed version flagged as the current version. Version numbers are derived from the order of
 * committed (succeeded) updates.
 */
function buildVersionRows(history: TimelineUpdateRead[]): VersionRow[] {
  const isActive = (u: TimelineUpdateRead) => kActiveTimelineUpdateStatuses.includes(u.status)
  const isFailed = (u: TimelineUpdateRead) => u.status === TimelineUpdateStatus.ERROR || u.status === TimelineUpdateStatus.FAILED_FILES
  const isCommitted = (u: TimelineUpdateRead) => !isActive(u) && !isFailed(u)

  const oldestFirst = [...history].sort((a, b) => a.created_at - b.created_at)
  const committedTotal = oldestFirst.filter(isCommitted).length

  let version = 0
  const rows = oldestFirst.map((u): VersionRow => {
    const active = isActive(u)
    const failed = isFailed(u)
    const committed = !active && !failed
    if (committed) version += 1
    const current = committed && version === committedTotal
    return {
      id: u.id,
      versionLabel: committed ? `v${version}` : null,
      when: formatVersionDateTime(u.completed_at ?? u.created_at),
      label: active ? describeActiveUpdate(u) : current ? 'Current version' : failed ? 'Failed' : describeUpdate(u),
      active,
      failed,
      current,
    }
  })
  return rows.reverse() // newest first for display
}

/**
 * Version-history sidebar: a 275px panel listing the timeline's committed versions — a version tag,
 * the timestamp, and the newest version flagged "Current version". Opened from the clock-rewind
 * control in the timeline action bar.
 */
export function TimelineVersionHistoryPopover({
  assetId,
  disabled = false,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  assetId: string
  disabled?: boolean
  /** Optional controlled open state (e.g. opened from the failed-files banner's "Version info"). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const dispatch = useAppDispatch()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openProp ?? internalOpen
  const history = useAppSelector((state: RootState) => selectUpdateHistoryForAsset(state, { assetId }))

  const onOpenChange = (next: boolean) => {
    if (openProp === undefined) setInternalOpen(next)
    onOpenChangeProp?.(next)
    if (next) dispatch(TimelineAssetActions.fetchUpdateHistory({ assetId }))
  }

  const rows = history ? buildVersionRows(history) : []

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <IconButton disabled={disabled} icon={<ClockRewindHistory />} variant="tonal" size="sm" title="Version history" aria-label="Version history" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 flex w-[275px] max-h-[70vh] flex-col overflow-hidden rounded-md border border-brand-neutral-200 bg-brand-neutral-50 shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)]"
          sideOffset={6}
          align="end"
        >
          {/* viewer-header: collapse control + title */}
          <div className="flex h-[60px] shrink-0 items-center gap-4 px-3 py-2">
            <Popover.Close asChild>
              <IconButton icon={<ContractAlignRight />} variant="tonal" size="xs" aria-label="Collapse version history" />
            </Popover.Close>
            <span className="text-sm font-medium tracking-[-0.15px] text-brand-neutral-950">Version History</span>
          </div>

          {history === null ? (
            <p className="px-4 py-3 text-xs text-brand-neutral-500">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-3 text-xs text-brand-neutral-500">No updates yet.</p>
          ) : (
            <ul className="flex flex-col gap-3 overflow-y-auto px-2 pb-3">
              {rows.map((row) => (
                <li key={row.id} className={`flex items-start gap-2 rounded-[4px] p-2 ${row.current ? 'bg-brand-neutral-200' : ''}`}>
                  {/* version tag */}
                  <span className="flex w-7 shrink-0 justify-start">
                    {row.active ? (
                      <span className="flex h-[16px] w-full items-center justify-center text-brand-neutral-500">
                        <CircularProgressContinuousSized size={12} thickness={4} />
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center justify-center rounded-[4px] border px-1 py-px text-[10.4px] font-medium leading-[11px] ${
                          row.current ? 'border-brand-200 bg-brand-100 text-brand-800' : 'border-brand-neutral-300 bg-brand-neutral-200 text-brand-neutral-600'
                        }`}
                      >
                        {row.versionLabel ?? '—'}
                      </span>
                    )}
                  </span>
                  {/* timestamp + secondary line */}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="whitespace-pre text-xs font-normal text-brand-neutral-950">{row.when}</span>
                    <span className={`text-xs ${row.current ? 'font-medium text-brand-800' : row.failed ? 'text-red-700' : 'text-brand-neutral-600'}`}>
                      {row.label}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
